import { BaseUser, DeepLink, IDEmployee } from '../core/apiTypes';
import { MAX_RECENT_PASSENGERS, NavTopBarHeight } from '../utils/constants';
import { isWebViewAndroid } from '../utils/webview';

const KEY_PREFIX = 'war_';
const KEY_PREFIX_WEBVIEW = 'wv_';

export const KEY_RECENT_RESET = `${KEY_PREFIX}recent_reset`;
export const KEY_RECENT_SEARCH = `${KEY_PREFIX}recent_search`;
export const KEY_RECENT_PASSENGER = `${KEY_PREFIX}recent_passengers`;
export const KEY_TABLE_FILTER = `${KEY_PREFIX}table_filter`;
export const KEY_LOCAL_STORAGE_GEOLOC = `${KEY_PREFIX}geoloc`;
export const KEY_LOCAL_STORAGE_CURRENT_TAB = `${KEY_PREFIX}currentTab`;
export const KEY_LOCAL_STORAGE_NATIVE_LAST_RIDE_FETCHED = `${KEY_PREFIX}native_last_ride_fetched`;
export const KEY_LOCAL_STORAGE_NATIVE_LAST_RIDE_RATED = `${KEY_PREFIX}native_last_ride_rated`;
export const KEY_LOCAL_STORAGE_NATIVE_OPEN = `${KEY_PREFIX}native_open`;
export const KEY_LOCAL_STORAGE_NATIVE_OFFLINE = `${KEY_PREFIX}native_offline`;
export const KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE = `${KEY_PREFIX}native_offline_banner_visible`;
export const KEY_LOCAL_STORAGE_NATIVE_DOWNLOAD_BANNER = `${KEY_PREFIX}native_download_banner`;
export const KEY_LOCAL_STORAGE_NATIVE_FOREGROUND = `${KEY_PREFIX}native_foreground`;
export const KEY_LOCAL_STORAGE_NATIVE_FOREGROUND_TIME = `${KEY_PREFIX}native_foreground_time`;
export const KEY_LOCAL_STORAGE_NATIVE_REFETCH_LOCATION = `${KEY_PREFIX}native_refetch_location`;
export const KEY_LOCAL_STORAGE_ONBOARDING_DONE = `${KEY_PREFIX}onboarding_done`;
export const KEY_SHOW_WICKED = `${KEY_PREFIX}wicked`;
export const KEY_SHOW_EXTERNAL = `${KEY_PREFIX}external`;
export const KEY_LISTING_PAGINATION = `${KEY_PREFIX}listing_pagination`;
const KEY_LOCAL_STORAGE_USER = `${KEY_PREFIX}user`;
const KEY_LOCAL_STORAGE_DEEP_LINK = `${KEY_PREFIX}deeplink`;

export const KEY_WV_STATUS_BAR = `${KEY_PREFIX}${KEY_PREFIX_WEBVIEW}shouldHandleStatusBar`;
export const KEY_WV_STATUS_BAR_HEIGHT = `${KEY_PREFIX}${KEY_PREFIX_WEBVIEW}statusBarHeight`;
export const KEY_WV_NAV_BAR_HEIGHT = `${KEY_PREFIX}${KEY_PREFIX_WEBVIEW}navBarHeight`;
export const KEY_WV_MOBILE_VERSION = `${KEY_PREFIX}${KEY_PREFIX_WEBVIEW}mobileVersion`;
export const KEY_WV_APP_FROM = `${KEY_PREFIX}${KEY_PREFIX_WEBVIEW}appFrom`;

interface AsyncStorageAPI {
    persistData: (key: string, data: any) => void;
    persistDataNoLink: (key: string, data: any) => void;
    restoreData: (key: string, defaultValue?: any) => any;
    restoreDataNoLink: (key: string, defaultValue?: any) => any;
    clearData: (key: string) => void;
    clearDataNoLink: (key: string) => void;
}

// Data written through persistData belongs to the signed-in user. Without an id we would fall
// back to a single shared `<key>_undefined` bucket, so whatever is written while logged out (or
// during the removeUser() teardown in redirectToAuth) would be read back by the next account
// signing in on this device. Skip instead of writing to that bucket.
const getUserScopedKey = (key: string): string => {
    const id = getUser()?.id;
    return id ? `${key}_${id}` : null;
};

const parseStoredValue = (item: string | null, defaultValue: any = null) => {
    if (item === null) return defaultValue; // Handle null case
    try {
        return JSON.parse(item);
    } catch (e) {
        return item; // Return the plain string if parsing fails
    }
};

export const asyncStorageApi: AsyncStorageAPI = {
    // Store
    persistData: (key: string, data: any) => {
        const scopedKey = getUserScopedKey(key);
        if (!scopedKey) {
            console.log(`persistData: no user id available, skipping ${key}`);
            return;
        }
        localStorage.setItem(scopedKey, data instanceof Object ? JSON.stringify(data) : data);
    },
    persistDataNoLink: (key: string, data: any) => {
        localStorage.setItem(key, data instanceof Object ? JSON.stringify(data) : data);
    },
    // Get
    restoreData: (key: string, defaultValue: any = null) => {
        const scopedKey = getUserScopedKey(key);
        if (!scopedKey) return defaultValue;

        return parseStoredValue(localStorage.getItem(scopedKey), defaultValue);
    },
    restoreDataNoLink: (key: string, defaultValue: any = null) => {
        if (key === KEY_WV_STATUS_BAR && isWebViewAndroid()) {
            return true;
        }

        return parseStoredValue(localStorage.getItem(key), defaultValue);
    },
    // Remove
    clearData: (key: string) => {
        const scopedKey = getUserScopedKey(key);
        if (scopedKey) {
            localStorage.removeItem(scopedKey);
        }
    },
    clearDataNoLink: (key: string) => {
        localStorage.removeItem(key);
    },
};

//= =====================================//
//= ============== GLOBAL ===============//
//= =====================================//

export const getTopBarHeight = (withBanner = false): number => {
    // return (
    //     parseInt('54', 10) +
    //     (asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE) && withBanner
    //         ? NavTopBarHeight
    //         : 0)
    // );
    return (
        (parseInt(asyncStorageApi.restoreDataNoLink(KEY_WV_STATUS_BAR_HEIGHT), 10) || 0) +
        (asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_NATIVE_OFFLINE_BANNER_VISIBLE) && withBanner
            ? NavTopBarHeight
            : 0)
    );
};

// Counterpart of getTopBarHeight for the bottom system bar (Android three-button bar or gesture
// handle). The native WebView is laid out edge to edge, so reserving this space is up to us.
export const getBottomBarHeight = (): number => {
    if (!isWebViewAndroid()) {
        return 0;
    }
    return parseInt(asyncStorageApi.restoreDataNoLink(KEY_WV_NAV_BAR_HEIGHT), 10) || 0;
};

//= ============================================//
//= ============== LOCAL STORAGE ===============//
//= ============================================//

export const getShowPopupGeoloc = (): any => {
    return Boolean(asyncStorageApi.restoreData(KEY_LOCAL_STORAGE_GEOLOC) ?? true);
};

export const saveShowPopupGeoloc = (data: any): any => {
    return asyncStorageApi.persistData(KEY_LOCAL_STORAGE_GEOLOC, data);
};

export const removeShowPopupGeoloc = (): any => {
    return asyncStorageApi.clearData(KEY_LOCAL_STORAGE_GEOLOC);
};

export const getCurrentTab = (): any => {
    const { pathname } = window.location;
    const tabs = asyncStorageApi.restoreData(KEY_LOCAL_STORAGE_CURRENT_TAB);
    return Number(tabs?.[pathname] ?? 0);
};

export const saveCurrentTab = (data: any, owPathname?: string): any => {
    const tabs = asyncStorageApi.restoreData(KEY_LOCAL_STORAGE_CURRENT_TAB) ?? {};
    const { pathname } = window.location;
    tabs[owPathname ?? pathname] = data;
    return asyncStorageApi.persistData(KEY_LOCAL_STORAGE_CURRENT_TAB, tabs);
};

// ====== DeepLink ====== //
export const getDeepLinkActions = (): DeepLink[] => {
    return [...(asyncStorageApi.restoreDataNoLink(KEY_LOCAL_STORAGE_DEEP_LINK) || [])];
};

export const saveDeepLinkActions = (data: DeepLink): void => {
    // Check mandatory params before saving
    const isPickupOrDropoffMissing = !data.pickup && !data.dropoff;
    const isLocationMissing = !data.pickUpLat || !data.pickUpLng || !data.dropOffLat || !data.dropOffLng;
    if (
        (data.action === 'promo' && !data.promocode) ||
        (data.action === 'book' && isPickupOrDropoffMissing && isLocationMissing)
    ) {
        return;
    }
    let actions = getDeepLinkActions();
    if (actions.find((a) => a.action === data.action)) {
        actions = actions.filter((dp) => dp.action !== data.action);
    }
    actions.push(data);
    asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_DEEP_LINK, JSON.stringify(actions));
};
export const removeDeepLinkActions = (action?: DeepLink['action']): void => {
    if (action) {
        const actions = getDeepLinkActions().filter((dp) => dp.action !== action);
        asyncStorageApi.persistDataNoLink(KEY_LOCAL_STORAGE_DEEP_LINK, JSON.stringify(actions));
    } else {
        asyncStorageApi.clearDataNoLink(KEY_LOCAL_STORAGE_DEEP_LINK);
    }
};

// ====== User ====== //
// Must never throw: it sits in the asyncStorageApi hot path (every scoped read and write calls
// it), so a half-written or corrupt entry would break unrelated storage access everywhere.
export const getUser = (): IDEmployee => {
    try {
        return JSON.parse(localStorage.getItem(KEY_LOCAL_STORAGE_USER)) || ({} as IDEmployee);
    } catch (error) {
        console.error('getUser: corrupt stored user, ignoring it', error?.message);
        return {} as IDEmployee;
    }
};
export const saveUser = (datas: any | IDEmployee): void => {
    localStorage.setItem(KEY_LOCAL_STORAGE_USER, datas instanceof Object ? JSON.stringify(datas) : datas);
};
export const removeUser = (): void => {
    localStorage.removeItem(KEY_LOCAL_STORAGE_USER);
};

export const getRecentPassengers = (): BaseUser[] => {
    return asyncStorageApi.restoreData(KEY_RECENT_PASSENGER) || [];
};
export const addRecentPassenger = (passenger: BaseUser[]): void => {
    const recentPassengers = [...(passenger ?? []), ...getRecentPassengers()].filter(
        (obj, index, self) => index === self.findIndex((o) => o.id === obj.id),
    );
    asyncStorageApi.persistData(KEY_RECENT_PASSENGER, recentPassengers.slice(0, MAX_RECENT_PASSENGERS));
};
export const updateRecentPassenger = (passenger: BaseUser[]): void => {
    asyncStorageApi.persistData(KEY_RECENT_PASSENGER, passenger.slice(0, MAX_RECENT_PASSENGERS));
};

//= ==============================================//
//= ============== SESSION STORAGE ===============//
//= ==============================================//

const KEY_SESSION_STORAGE_LAST_LOCATION = `${KEY_PREFIX}lastLocation`;

export const getLastLocation = (): any => {
    return JSON.parse(sessionStorage.getItem(KEY_SESSION_STORAGE_LAST_LOCATION));
};

export const saveLastLocation = (data: any): void => {
    sessionStorage.setItem(KEY_SESSION_STORAGE_LAST_LOCATION, JSON.stringify(data));
};
export const removeLastLocation = (): void => {
    sessionStorage.removeItem(KEY_SESSION_STORAGE_LAST_LOCATION);
};
